import { readPreparedExport, type PreparedCampaignExport } from '../lib/campaign-export-filters';
import { readChartPayload, type ChartPayload } from '../lib/campaign-analytics/payload';
/**
 * Gemini Service - Frontend API Client
 * 
 * Provides client-side interface for Gemini AI services.
 * All API calls are proxied through Cloudflare Workers/Pages Functions.
 * 
 * SECURITY: API keys are NEVER exposed in the frontend. All sensitive
 * operations happen server-side in the workers.
 * 
 * @module services/geminiService
 */

import type { ProductResearch } from '../types';
import { captureException } from '../lib/telemetry/posthog/frontend';

/**
 * Translation response from the API
 */
export interface TranslationResponse {
  success: boolean;
  original_language?: string;
  translated_language?: string;
  translated_turns?: {
    speaker: string;
    text: string;
    timestamp?: number;
  }[];
  error?: string;
  message?: string;
}

/**
 * Translate an interview's conversation to a target language
 * SECURITY: Requires authenticated user - passes Supabase JWT
 * @param interviewId - The interview session ID
 * @param targetLanguage - BCP-47 language code (e.g., 'en-US', 'es-ES')
 * @param authToken - Supabase JWT token
 */
export async function translateInterview(
  interviewId: string,
  targetLanguage: string,
  authToken: string
): Promise<TranslationResponse> {
  if (!authToken) {
    throw new Error('Authentication required for translation');
  }

  const response = await fetch('/api/gemini/translate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      interview_id: interviewId,
      target_language: targetLanguage
    }),
  });

  const data = await response.json() as TranslationResponse;

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Translation failed');
  }

  return data;
}

/**
 * Theme generation response from the API
 */
export interface GenerateThemesResponse {
  themes: string[];
}

/** Optional request settings for theme generation. */
export interface GenerateThemesOptions {
  /** Active organization ID used for server-side entitlement checks. */
  organizationId?: string | null;
  /** Requested maximum number of themes before server-side plan clamping. */
  maxThemes?: number;
}

/**
 * Generate 12 interview themes/topics based on the business objective
 * Returns themes in narrative order for natural conversation flow
 * SECURITY: Requires authenticated user - passes Supabase JWT
 * @param goal - Business objective/goal for the interview
 * @param targetName - Product/company name for context
 * @param productResearch - Optional product research for additional context
 * @param authToken - Supabase JWT token
 * @param options - Optional org context and requested theme-count hint
 */
export async function generateThemes(
  goal: string,
  targetName: string,
  productResearch: ProductResearch | null,
  authToken: string,
  options?: GenerateThemesOptions,
): Promise<string[]> {
  if (!authToken) {
    throw new Error('Authentication required for theme generation');
  }

  if (!goal.trim()) {
    throw new Error('Interview objective is required to generate themes');
  }

  const response = await fetch('/api/gemini/generate-themes', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
      ...(options?.organizationId ? { 'X-Organization-ID': options.organizationId } : {}),
    },
    body: JSON.stringify({
      goal,
      targetName,
      productResearch,
      ...(options?.maxThemes !== undefined && { maxThemes: options.maxThemes }),
    }),
  });

  if (!response.ok) {
    const error = (await response
      .json()
      .catch(() => ({ error: 'Unknown error' }))) as { error?: string };
    throw new Error(error.error || 'Theme generation failed');
  }

  const data = await response.json() as GenerateThemesResponse;
  return data.themes || [];
}

/**
 * Theme optimization response from the API
 */
export interface OptimizeThemesResponse {
  optimizedDetails: string;
}

/**
 * Optimize interview theme order for better conversational flow
 * Reorders themes into a logical sequence (rapport → experience → impact → forward-looking)
 * SECURITY: Requires authenticated user - passes Supabase JWT
 * @param details - Semicolon-separated themes/topics
 * @param goal - Interview goal for context
 * @param targetName - Product/company name for context
 * @param authToken - Supabase JWT token
 */
export async function optimizeThemeOrder(
  details: string,
  goal: string,
  targetName: string,
  authToken: string
): Promise<string> {
  if (!authToken) {
    throw new Error('Authentication required for theme optimization');
  }

  // Skip optimization if details are empty or very short
  if (!details || details.split(';').filter(t => t.trim()).length <= 3) {
    return details;
  }

  try {
    const response = await fetch('/api/gemini/optimize-themes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        details,
        goal,
        targetName
      }),
    });

    if (!response.ok) {
      console.error('Theme optimization failed, using original order');
      return details;
    }

    const data = await response.json() as OptimizeThemesResponse;
    return data.optimizedDetails || details;
  } catch (error) {
    console.error('Theme optimization error:', error);
    captureException(error instanceof Error ? error : String(error), 'GeminiService.optimizeThemes', { goal, targetName });
    // Return original on error - optimization is non-critical
    return details;
  }
}

/**
 * A grounded citation pointing back to a specific turn in one completed
 * conversation that supports the answer.
 */
export interface Citation {
  /** The conversation (interview_data) row the quote came from. */
  conversation_id: string;
  /** Zero-based index of the turn within that conversation's transcript. */
  turn_index: number;
  /** The exact supporting quote pulled from the transcript. */
  quote: string;
  /** Human-facing response number for this conversation within the campaign. */
  response_number: number;
  /** Display name of the respondent (or an anonymous placeholder). */
  respondent_name: string;
  /** Millisecond offset of the turn in the recording, when known. */
  turn_timestamp_ms: number | null;
}

/** One prior turn of the ask-the-campaign chat thread, sent for follow-up context. */
export interface AskCampaignChatTurn {
  /** Who produced the turn. */
  role: 'user' | 'assistant';
  /** The turn text (question or answer). */
  content: string;
}

/**
 * One tool the ask agent invoked, as surfaced live (and persisted for replay).
 * `id` correlates a call with its result while streaming and is dropped on
 * persistence; `ok` / `preview` arrive with the result.
 */
export interface AskToolCall {
  /** Correlates a tool_call with its tool_result during streaming (ephemeral). */
  id?: string;
  /** The tool the agent invoked (e.g. search_conversations). */
  name: string;
  /** The arguments the agent passed (e.g. the search query). */
  args?: Record<string, unknown>;
  /** Whether the tool succeeded (set when the result arrives). */
  ok?: boolean;
  /** A short, payload-free outcome label (set when the result arrives). */
  preview?: string;
  /** Server-prepared, campaign-bound CSV download preferences. */
  csvExport?: PreparedCampaignExport;
  /** A validated chart card built server-side from stored answers. */
  chart?: ChartPayload;
}

/** Tools whose results may carry a chart card. */
const CHART_TOOLS = new Set(['show_question_chart', 'compare_questions']);

/** Model lifecycle phases, separate from private reasoning text. */
export type AskActivity = 'thinking' | 'reviewing' | 'answering';

/** Streaming callbacks for {@link askCampaignStream}. */
export interface AskCampaignStreamHandlers {
  /** Fired before a real model round starts, without waiting for generated text. */
  onActivity?: (phase: AskActivity) => void;
  /** Fired with each incremental answer fragment as it streams in. */
  onToken: (text: string) => void;
  /** Fired once with the grounded citations after the answer completes. */
  onCitations: (citations: Citation[]) => void;
  /** Fired with each fragment of the agent's reasoning as it streams in. */
  onThinking?: (text: string) => void;
  /** Fired when the agent invokes a tool. */
  onToolCall?: (call: { id: string; name: string; args?: Record<string, unknown> }) => void;
  /** Fired when a tool the agent invoked returns. */
  onToolResult?: (result: { id: string; name: string; ok: boolean; preview: string; csvExport?: PreparedCampaignExport; chart?: ChartPayload }) => void;
  /** Fired once with an AI-generated short title for a new thread (first turn only). */
  onTitle?: (title: string) => void;
}

/** Optional request settings for {@link askCampaignStream}. */
export interface AskCampaignStreamOptions {
  /** Active organization ID used for server-side entitlement checks. */
  organizationId?: string;
  /** Restrict the search to specific conversations (interview_data IDs). */
  interviewDataIds?: string[] | null;
  /** Prior turns of the thread, oldest first, for follow-up context. */
  history?: AskCampaignChatTurn[];
  /** Abort signal to cancel an in-flight stream. */
  signal?: AbortSignal;
}

/**
 * Ask a question across a campaign's completed conversations and STREAM a
 * grounded, cited answer chat-style. Tokens arrive incrementally via
 * `onToken`; the grounded citations arrive once at the end via `onCitations`.
 * Prior thread turns can be passed for follow-up context.
 *
 * SECURITY: Requires authenticated user - passes Supabase JWT.
 * @param interviewId - The interview session (campaign) ID
 * @param question - The natural-language question to ask
 * @param authToken - Supabase JWT token
 * @param handlers - Token and citation callbacks
 * @param options - Optional org context, conversation-scope, history, abort signal
 * @throws Error if auth/question is missing, the request fails, or generation errors mid-stream
 */
export async function askCampaignStream(
  interviewId: string,
  question: string,
  authToken: string,
  handlers: AskCampaignStreamHandlers,
  options?: AskCampaignStreamOptions,
): Promise<void> {
  if (!authToken) {
    throw new Error('Authentication required');
  }

  if (!question.trim()) {
    throw new Error('Question is required');
  }

  const response = await fetch('/api/gemini/ask-campaign', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
      ...(options?.organizationId ? { 'X-Organization-ID': options.organizationId } : {}),
    },
    body: JSON.stringify({
      interview_id: interviewId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      question,
      ...(options?.interviewDataIds ? { interview_data_ids: options.interviewDataIds } : {}),
      ...(options?.history?.length ? { history: options.history } : {}),
    }),
    ...(options?.signal ? { signal: options.signal } : {}),
  });

  if (!response.ok || !response.body) {
    const data = (await response
      .json()
      .catch(() => ({ error: 'Unknown error' }))) as { error?: string };
    throw new Error(data.error || 'Ask failed');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let streamError: string | null = null;
  let stop = false;

  // SSE frames are separated by a blank line; each carries one `data: <json>`.
  while (!stop) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() || ''; // keep the trailing partial frame

    for (const frame of frames) {
      const line = frame.trim();
      if (!line.startsWith('data: ')) continue;
      let event: {
        type?: string;
        text?: string;
        citations?: Citation[];
        error?: string;
        id?: string;
        name?: string;
        args?: Record<string, unknown>;
        ok?: boolean;
        preview?: string;
        title?: string;
        csvExport?: unknown;
        chart?: unknown;
        phase?: unknown;
      };
      try {
        event = JSON.parse(line.slice('data: '.length));
      } catch {
        continue; // skip a malformed frame rather than aborting the whole stream
      }
      if (event.type === 'activity' && (event.phase === 'thinking' || event.phase === 'reviewing' || event.phase === 'answering')) {
        handlers.onActivity?.(event.phase);
      } else if (event.type === 'token' && typeof event.text === 'string') {
        handlers.onToken(event.text);
      } else if (event.type === 'title' && typeof event.title === 'string') {
        handlers.onTitle?.(event.title);
      } else if (event.type === 'thinking' && typeof event.text === 'string') {
        handlers.onThinking?.(event.text);
      } else if (event.type === 'tool_call' && typeof event.name === 'string') {
        handlers.onToolCall?.({ id: event.id ?? '', name: event.name, args: event.args });
      } else if (event.type === 'tool_result' && typeof event.name === 'string') {
        const csvExport = event.ok && event.name === 'export_campaign_csv' ? readPreparedExport(event.csvExport, interviewId) : undefined;
        const chart = event.ok && CHART_TOOLS.has(event.name) ? readChartPayload(event.chart) : null;
        handlers.onToolResult?.({
          id: event.id ?? '',
          name: event.name,
          ok: event.ok ?? false,
          preview: typeof event.preview === 'string' ? event.preview : '',
          ...(csvExport ? { csvExport } : {}),
          ...(chart ? { chart } : {}),
        });
      } else if (event.type === 'citations') {
        handlers.onCitations(event.citations ?? []);
      } else if (event.type === 'error') {
        // Surface the failure and stop consuming so no post-error tokens land.
        streamError = event.error || 'Generation failed';
        stop = true;
        break;
      }
    }
  }

  if (stop) {
    // Release the body without awaiting (cancel can hang on some stream impls).
    void reader.cancel().catch(() => {});
  }
  if (streamError) {
    throw new Error(streamError);
  }
}

/**
 * Content types available for generation. `custom` is the pipeline type a
 * user-authored recipe runs as (the recipe id, not the type, identifies it).
 */
export type ContentType = 'case_study' | 'expert_article' | 'report' | 'campaign_report' | 'custom';

/** Stable public failure categories returned by recipe generation. */
export type CaseStudyErrorCode =
  | 'transcript_empty'
  | 'output_not_reliable'
  | 'generation_failed';

/** Check whether an untrusted API value is a supported recipe failure code. */
function isCaseStudyErrorCode(value: unknown): value is CaseStudyErrorCode {
  return value === 'transcript_empty' ||
    value === 'output_not_reliable' ||
    value === 'generation_failed';
}

/**
 * Case study generation response from the API.
 * Contains the generated HTML content and metadata.
 */
export interface CaseStudyResponse {
  /** Whether the generation was successful */
  success: boolean;
  /** Database ID of the saved case study */
  case_study_id?: string;
  /** HTML-formatted case study content */
  content?: string;
  /** Error message if generation failed */
  error?: string;
  /** Stable failure code used for safe UI presentation */
  error_code?: CaseStudyErrorCode;
  /** Whether generation is currently in progress server-side */
  in_progress?: boolean;
  /** Optional server generation status for resume UI */
  generation_state?: {
    jobId?: string;
    startedAt: number;
    lastHeartbeat: number;
    stage: string;
    contentType: ContentType;
  };
}

/** Stop trusting an idle connection and recover from the persisted generation state. */
export const CASE_STUDY_STREAM_IDLE_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Generates content from an interview transcript.
 * Calls the Cloudflare Function endpoint which uses Gemini AI to transform
 * the interview into a structured narrative based on the content type.
 *
 * Returns cached content as JSON immediately, or streams SSE progress
 * events during generation. The onProgress callback fires with real
 * pipeline stage names as the backend progresses.
 *
 * @param interviewId - The interview session ID to generate content from
 * @param authToken - Supabase JWT token for authentication
 * @param contentType - Type of content to generate (case_study, expert_article, or report)
 * @param onProgress - Optional callback fired with pipeline stage name during generation
 * @param responseId - Optional response (interview_data) id to scope generation to one conversation
 * @param recipeId - Optional recipe id; when provided the server resolves the recipe (built-in or custom) and `content_type` is only a backward-compat hint
 * @returns Promise resolving to the response with HTML content
 * @throws Error if authentication fails, interview not found, or generation fails
 */
export async function generateCaseStudy(
  interviewId: string,
  authToken: string,
  contentType: ContentType = 'case_study',
  onProgress?: (stage: string) => void,
  responseId?: string,
  recipeId?: string,
  force?: boolean,
): Promise<CaseStudyResponse> {
  if (!authToken) {
    throw new Error('Authentication required for case study generation');
  }

  if (!interviewId) {
    throw new Error('Interview ID is required');
  }

  const response = await fetch('/api/gemini/generate-case-study', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      interview_id: interviewId,
      content_type: contentType,
      ...(responseId && { interview_data_id: responseId }),
      ...(recipeId && { recipe_id: recipeId }),
      // Regenerate: skip the stored-content short-circuit and overwrite in place.
      ...(force && { force: true }),
    }),
  });

  const responseType = response.headers.get('Content-Type') || '';

  // Cached content returns as JSON
  if (responseType.includes('application/json')) {
    const data = await response.json() as CaseStudyResponse;
    if (!response.ok && data.in_progress) {
      return data;
    }
    if (!response.ok || !data.success) {
      if (isCaseStudyErrorCode(data.error_code)) {
        return { success: false, error_code: data.error_code };
      }
      throw new Error(data.error || 'Case study generation failed');
    }
    return data;
  }

  // Pipeline generation streams SSE events
  if (responseType.includes('text/event-stream') && response.body) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: CaseStudyResponse | null = null;

    try {
      while (!result) {
        let idleTimer: ReturnType<typeof setTimeout> | undefined;
        let chunk: ReadableStreamReadResult<Uint8Array>;
        try {
          chunk = await Promise.race([
            reader.read(),
            new Promise<never>((_, reject) => {
              idleTimer = setTimeout(() => reject(new Error('Recipe progress stream became idle')),
                CASE_STUDY_STREAM_IDLE_TIMEOUT_MS);
            }),
          ]);
        } catch (error) {
          captureException(error instanceof Error ? error : new Error('Recipe progress stream disconnected'),
            'geminiService.generateCaseStudy.streamRecovery', {
              interviewId, responseId, recipeId,
            });
          return { success: false, in_progress: true };
        } finally {
          clearTimeout(idleTimer);
        }
        const { done, value } = chunk;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'progress') {
              onProgress?.(data.stage);
            } else if (data.type === 'result') {
              result = data as CaseStudyResponse;
            } else if (data.type === 'error') {
              if (isCaseStudyErrorCode(data.error_code)) {
                result = { success: false, error_code: data.error_code };
              } else {
                throw new Error(data.error || 'Generation failed');
              }
            }
          } catch (e) {
            if (e instanceof SyntaxError) continue; // Skip malformed JSON lines
            throw e;
          }
          if (result) break;
        }
      }
    } finally {
      // A result is terminal even if the transport remains open. Cancellation
      // must not delay completion on implementations with a stalled cancel.
      void reader.cancel().catch(() => {});
    }

    if (!result) {
      captureException(new Error('Recipe stream ended without a result'),
        'geminiService.generateCaseStudy.streamRecovery', { interviewId, responseId, recipeId });
      return { success: false, in_progress: true };
    }
    if (!result.success) {
      if (isCaseStudyErrorCode(result.error_code)) return result;
      throw new Error(result.error || 'Generation failed');
    }
    return result;
  }

  // Fallback for unexpected response format
  throw new Error('Unexpected response format');
}
